// ============================================================================
// ACTIVITY HOOKS
// ============================================================================
// React hooks for the Activity master.
// Pattern follows useEngagementTypes.ts exactly.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { activityService } from 'api/services/activityService';
import type {
  ID,
  PaginatedResponse,
  Activity,
  CreateActivityRequest,
  UpdateActivityRequest,
  ActivitySearchRequest,
} from 'api/types';

const EMPTY_PAGINATION = {
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

/**
 * Hook to fetch a paginated list of activities.
 * Refetches automatically when `params` changes.
 */
export const useActivities = (params?: ActivitySearchRequest) => {
  const [data, setData] = useState<Activity[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<Activity>['pagination']>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await activityService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  return { data, pagination, loading, error, refetch: fetchActivities };
};

/**
 * Hook to fetch a single activity by id.
 */
export const useActivity = (id: ID | null | undefined) => {
  const [data, setData] = useState<Activity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchActivity = useCallback(async () => {
    if (!id) { setData(null); return; }
    setLoading(true);
    setError(null);
    try {
      const response = await activityService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  return { data, loading, error, refetch: fetchActivity };
};

/**
 * Hook to create a new activity.
 */
export const useCreateActivity = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createActivity = async (
    data: CreateActivityRequest,
    options?: { onSuccess?: (item: Activity) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await activityService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { createActivity, loading, error, success };
};

/**
 * Hook to update an activity. Always pass the latest rowVersion.
 */
export const useUpdateActivity = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateActivity = async (
    id: ID,
    data: UpdateActivityRequest,
    options?: { onSuccess?: (item: Activity) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await activityService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { updateActivity, loading, error, success };
};

/**
 * Hook to delete an activity (soft-delete on the server).
 */
export const useDeleteActivity = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteActivity = async (
    id: ID,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await activityService.delete(id);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { deleteActivity, loading, error, success };
};
